import { faCar, faPersonWalking } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import LoadingSpinner from 'components/LoadingSpinner';
import SiteMapModal from 'components/modal/SiteMapModal';
import SideMenu from 'features/SideMenu';
import { setCurrInfoWindow, setCurrPOIs, setInfoWindowDetails } from 'features/vicinityMapSlice';
import config from 'lib/config';
import { DEFAULT_MAP_THEME, DISABLE_GOOGLE_POIS_MAP_THEME, GM_LIBRARIES } from 'lib/constants';
import gaFunction from 'lib/ga';
import { useVicinityMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import type { IPOI } from 'lib/models/vicinityMap.interface';
import {
  getAllPOIs,
  getCategory,
  getGMCoordinates,
  getInfoWindowTextWidth,
  initSMOverlay,
  renderDirections,
  resetGoogleMap,
  setCommunityLogoStyles,
} from 'lib/utils';
import { useState } from 'react';
import styled from 'styled-components';
import Nav from 'components/Nav';

function Map() {
  const dispatch = useAppDispatch();
  const { currPOIs, currInfoWindow, infoWindowDetails } = useAppSelector((state) => ({
    currPOIs: state.vicinityMap.currPOIs,
    currInfoWindow: state.vicinityMap.currInfoWindow,
    infoWindowDetails: state.vicinityMap.infoWindowDetails,
  }));

  const vicinityMapData = useVicinityMapQuery().data!;

  const [map, setMap] = useState<google.maps.Map>();
  const [showSiteMapModal, setShowSiteMapModal] = useState(false);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer>();

  // load the map using API key
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries: GM_LIBRARIES,
  });

  // pan to the center of the map on load
  const onMapLoad = (map: google.maps.Map) => {
    setMap(map);
    setDirectionsRenderer(
      new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
      })
    );

    // init poi state with all pois
    dispatch(setCurrPOIs(getAllPOIs(vicinityMapData.categories)));

    // if the northEast and southWest bounds are defined, restrict the map to those bounds
    if (vicinityMapData.main.northEastCorner && vicinityMapData.main.southWestCorner) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(getGMCoordinates(vicinityMapData.main.northEastCorner));
      bounds.extend(getGMCoordinates(vicinityMapData.main.southWestCorner));
      map.set('restriction', {
        latLngBounds: bounds,
        strictBounds: false,
      });
    }

    resetGoogleMap({ map, data: vicinityMapData });

    if (
      vicinityMapData.main.smOverlaySouthWestCoordinates &&
      vicinityMapData.main.smOverlayNorthEastCoordinates &&
      vicinityMapData.main.smOverlayComAsset
    ) {
      initSMOverlay({
        map,
        bounds: new google.maps.LatLngBounds(
          new google.maps.LatLng({
            lat: Number(vicinityMapData.main.smOverlaySouthWestCoordinates.split(',')[0]),
            lng: Number(vicinityMapData.main.smOverlaySouthWestCoordinates.split(',')[1]),
          }),
          new google.maps.LatLng({
            lat: Number(vicinityMapData.main.smOverlayNorthEastCoordinates.split(',')[0]),
            lng: Number(vicinityMapData.main.smOverlayNorthEastCoordinates.split(',')[1]),
          })
        ),
        image: `${vicinityMapData.systemProperties.BASE_ASSET_URL}/${vicinityMapData.community.applicationID}/${vicinityMapData.main.smOverlayComAsset.folder}/${vicinityMapData.main.smOverlayComAsset.fileName}`,
        rotate: vicinityMapData.main.smOverlayRotate || 0,
        onClick: () => setShowSiteMapModal(true),
      });
    }
  };

  const onMarkerClick = (POI: IPOI) => {
    // GA
    gaFunction.vmSelectPlacemarkMap(
      getCategory(POI.vicCategorySysId!, vicinityMapData)
        ? getCategory(POI.vicCategorySysId!, vicinityMapData)!.title
        : 'n/a',
      POI.title
    );

    if (directionsRenderer && map) {
      // render directions polyline on map
      renderDirections({
        directionsRenderer,
        POI,
        origin: getGMCoordinates(vicinityMapData.main.projectLocationCoordinates),
        map,
        dispatch,
      });
    }
  };

  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <GoogleMap
      id="cy-google-map"
      mapContainerStyle={containerStyle}
      // if jsonStyles are defined, use them, otherwise use default silver map theme
      options={{
        styles: vicinityMapData.main.jsonStyles
          ? [...JSON.parse(vicinityMapData.main.jsonStyles), ...DISABLE_GOOGLE_POIS_MAP_THEME]
          : DEFAULT_MAP_THEME,
        gestureHandling: 'cooperative',
        // mapId: !vicinityMapData.main.jsonStyles ? '418e07f20a0aeed9' : undefined,
        fullscreenControl: false,
        mapTypeControl: false,
        zoomControl: false,
        minZoom: 4,
      }}
      onLoad={onMapLoad}
    >
      <Nav map={map} />
      {/* add marker for project location */}
      {map && vicinityMapData.main.prjLogoComAsset && (
        <InfoWindow
          position={getGMCoordinates(vicinityMapData.main.projectLocationCoordinates)}
          zIndex={100000}
          onDomReady={() => setCommunityLogoStyles(document.querySelector('#community-logo')!)}
        >
          <StyledCommunityLogo
            src={`${vicinityMapData.systemProperties.BASE_ASSET_URL}/${vicinityMapData.community.applicationID}/${vicinityMapData.main.prjLogoComAsset.folder}/${vicinityMapData.main.prjLogoComAsset.fileName}`}
            id="community-logo"
            className="cy-community-logo"
            data-cy="community-logo"
            alt={`${vicinityMapData.community.name} Logo`}
          />
        </InfoWindow>
      )}
      {/* add markers for POIs */}
      {map &&
        currPOIs &&
        currPOIs.map((POI) => (
          <Marker
            key={POI.sysId}
            position={getGMCoordinates(POI.coordinates)}
            onClick={() => onMarkerClick(POI)}
            // if POI has a marker use it, else if the category has a marker use it, else use gm default
            icon={
              POI.markerUrl
                ? `${vicinityMapData.systemProperties.BASE_ASSET_URL}${POI.markerUrl}`
                : POI.vicCategorySysId &&
                  getCategory(POI.vicCategorySysId, vicinityMapData)?.markerUrl
                ? `${vicinityMapData.systemProperties.BASE_ASSET_URL}${
                    getCategory(POI.vicCategorySysId, vicinityMapData)!.markerUrl
                  }`
                : undefined
            }
            title={`marker_${POI.sysId}`}
          />
        ))}
      {/* add info window for selected POI */}
      {currInfoWindow && (
        <InfoWindow
          position={currInfoWindow.position}
          onCloseClick={() => dispatch(setCurrInfoWindow())}
        >
          <InfoWindowContainer id="infoWindowContent" data-cy="info-window-content">
            {currInfoWindow.image && (
              <img
                src={`${vicinityMapData.systemProperties.BASE_ASSET_URL}/${vicinityMapData.community.applicationID}${currInfoWindow.image}`}
                alt={`${currInfoWindow.content.title}`}
                style={{
                  width: infoWindowDetails
                    ? getInfoWindowTextWidth(Object.values(currInfoWindow.content))
                    : Math.max(getInfoWindowTextWidth([currInfoWindow.content.title]), 93),
                  borderRadius: '8px',
                }}
                onError={() => dispatch(setCurrInfoWindow({ ...currInfoWindow, image: undefined }))}
              />
            )}
            <span>{currInfoWindow.content.title}</span>
            {infoWindowDetails ? (
              <>
                <span>{currInfoWindow.content.address}</span>
                <span>
                  {currInfoWindow.mode === google.maps.TravelMode.DRIVING || null ? (
                    <FontAwesomeIcon icon={faCar} />
                  ) : (
                    <FontAwesomeIcon icon={faPersonWalking} />
                  )}{' '}
                  {currInfoWindow.content.directions}
                </span>
              </>
            ) : (
              <MoreInfoButton
                id="more-info-button"
                data-cy="more-info-button"
                onClick={() => {
                  dispatch(setInfoWindowDetails(true));
                  if (currInfoWindow.poiCategory && currInfoWindow.poiTitle) {
                    gaFunction.vmViewPlacemarkDetails(
                      getCategory(currInfoWindow.poiCategory, vicinityMapData)
                        ? getCategory(currInfoWindow.poiCategory, vicinityMapData)!.title
                        : 'n/a',
                      currInfoWindow.poiTitle
                    );
                  }
                }}
              >
                More Details
              </MoreInfoButton>
            )}
          </InfoWindowContainer>
        </InfoWindow>
      )}
      {/* render the sideMenu only if these variables have defined values */}
      {map && currPOIs && directionsRenderer && (
        <SideMenu map={map} directionsRenderer={directionsRenderer} />
      )}
      {/* only shown if there is an overlay that is clicked, see initSMOverlay */}
      <SiteMapModal showSiteMapModal={showSiteMapModal} setShowSiteMapModal={setShowSiteMapModal} />
    </GoogleMap>
  );
}

export default Map;

const containerStyle = {
  width: '100%',
  height: '100%',
};

const StyledCommunityLogo = styled.img`
  width: ${({ theme }) => theme.layout.logoWidth}px;
`;

const InfoWindowContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const MoreInfoButton = styled.button`
  color: ${({ theme }) => theme.colors.primaryTextColor};
  background-color: ${({ theme }) => theme.colors.primaryColor};
  border: none;
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
